import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import axiosInstance from "../api/axiosInstance";
import NumberFormatter from "../utils/NumberFormatter";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";


/**
 * MrrCohortsChart
 * Props:
 *  - months  (number) default 18
 *  - teamId  (number|undefined)
 *  - planId  (number|undefined)
 *
 * Backend endpoints expected:
 *  GET /api/mrr/cohorts?months=N[&team_id=...&plan_id=...]
 *  GET /api/mrr/cohorts/details?month=YYYY-MM&category=New|Expansion|Churned[&team_id=...&plan_id=...]
 */
export default function MrrCohortsChart({ months = 18, teamId, planId, excludeCurrentMonth = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detail, setDetail] = useState(null); // { month, category, items: [], error? }
  const [detailLoading, setDetailLoading] = useState(false);

  // Build params for both requests
  const baseParams = useMemo(() => {
    const p = { months };
    if (teamId != null) p.team_id = teamId;
    if (planId != null) p.plan_id = planId;
    if (excludeCurrentMonth) p.exclude_current_month = 1;
    return p;
  }, [months, teamId, planId, excludeCurrentMonth]);

  // ---- load aggregate series ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get("/api/mrr/cohorts", { params: baseParams });
        if (!cancelled) setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load MRR cohorts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [baseParams]);

  const categories = rows.map((r) => r.month);
  const sNew = rows.map((r) => Number(r.new_mrr || 0));
  const sExpansion = rows.map((r) => Number(r.expansion_mrr || 0));
  // Plot contraction (downgrades) and churn as negative so the stack visually matches Net
  const sContractionPlot = rows.map((r) => -Number(r.contraction_mrr || 0));
  const sChurnPlot = rows.map((r) => -Number(r.churned_mrr || 0));
  const sNet = rows.map((r) => Number(r.net_mrr || 0));

  const currency0 = (n) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

  // ---- drilldown ----
  const fetchDetails = async (month, category) => {
    setDetailLoading(true);
    try {
      const params = { month, category };
      if (teamId != null) params.team_id = teamId;
      if (planId != null) params.plan_id = planId;

      const res = await axiosInstance.get("/api/mrr/cohorts/details", { params });
      setDetail({ month, category, items: res.data?.data || [] });
    } catch (e) {
      setDetail({ month, category, items: [], error: e?.message || "Failed to load details" });
    } finally {
      setDetailLoading(false);
    }
  };

  const options = useMemo(() => ({
    chart: { 
      type: "column", 
      height: 520, 
      style: { fontFamily: "Montserrat, system-ui, sans-serif" },
    },
    title: { text: "MRR Changes (New, Expansion, Contraction, Churned) + Net" },
    credits: { enabled: false },
    xAxis: { categories, crosshair: true },
    yAxis: [{ title: { text: "MRR Change (USD)" }, plotLines: [{ value: 0, width: 1 }] }],
    legend: { enabled: true },

    exporting: {
      enabled: true,
    },
    
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        const idx = this.points?.[0]?.point?.index ?? 0;
        const lines = [`<b>${this.x}</b>`];
        this.points.forEach((pt) => {
          // flip contraction/churn back to positive magnitudes for display
          const shown = (pt.series.name === "Churned" || pt.series.name === "Contraction") ? -pt.y : pt.y;
          lines.push(`${pt.series.name}: <b>${currency0(shown)}</b>`);
        });
        lines.push(`<span style="color:gray">Net: <b>${currency0(sNet[idx] || 0)}</b></span>`);
        return lines.join("<br/>");
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        cursor: "pointer",
        borderWidth:0,
        dataLabels: { enabled: false },
        point: {
          events: {
            click: function () {
              const month = categories[this.index];
              const category = this.series.name; // 'New' | 'Expansion' | 'Churned'
              fetchDetails(month, category);
            },
          },
        },
      },
      series: { animation: true },
    },
    series: [
      { name: "New", type: "column", data: sNew, color: "#28277e"  },
      { name: "Expansion", type: "column", data: sExpansion , color: "#6d5bd1"},
      { name: "Contraction", type: "column", data: sContractionPlot, color: "#f0a202" }, // plotted negative
      { name: "Churned", type: "column", data: sChurnPlot, color:"#ff495e"}, // plotted negative
      { name: "Net", type: "line", data: sNet, color: "#aec8fa", dataLabels: {
        enabled: true
        }, },
    ],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [JSON.stringify(categories), JSON.stringify(sNew), JSON.stringify(sExpansion), JSON.stringify(sContractionPlot), JSON.stringify(sChurnPlot), JSON.stringify(sNet)]);

  return (
    <div>
      <div>
        {loading ? (
          <div>Loading…</div>
        ) : error ? (
          <div>{error}</div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={options} />
        )}
      </div>

      {/* Drilldown modal */}
      {detail && (
        <div onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <div>
              <div>
                <h3>{detail.category} - {detail.month}</h3>
              </div>
            </div>

            <div>
              {detailLoading ? (
                <div>Loading…</div>
              ) : detail.error ? (
                <div>{detail.error}</div>
              ) : detail.items.length === 0 ? (
                <div>No accounts</div>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                  <Table stickyHeader size="small" aria-label="MRR detail table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Team</TableCell>
                        <TableCell>Prev MRR</TableCell>
                        <TableCell>Curr MRR</TableCell>
                        <TableCell align="right">Change</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.items.map((row, i) => (
                        <TableRow key={i} hover>
                          <TableCell><a href={`/team/${row.team_id}`}>{row.team_name}</a>
                          </TableCell>
                          <TableCell>{NumberFormatter.formatCurrency(row.prev_mrr)}</TableCell>
                          <TableCell>{NumberFormatter.formatCurrency(row.curr_mrr)}</TableCell>
                          <TableCell
                            align="right"
                            sx={(theme) => ({
                              color:
                                row.change >= 0
                                  ? theme.palette.success.dark
                                  : theme.palette.error.dark,
                              fontWeight: 600,
                            })}
                          >
                            {row.change >= 0 ? "+" : ""}
                            {NumberFormatter.formatCurrency(row.change)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
            <button onClick={() => setDetail(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
